import { Component, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { map } from 'rxjs';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { ButtonComponent } from '@shared/components/button';
import { FieldErrorDirective, FieldHintDirective, FormFieldComponent } from '@shared/components/form-field';
import { InputComponent } from '@shared/components/input';
import { ModalFooterDirective, ModalShellComponent } from '@shared/components/modal';
import { SelectComponent } from '@shared/components/select';
import { ToggleComponent } from '@shared/components/toggle';
import { CreationClient, LONGUEURS_MAXIMALES } from '../liste-clients.interface';

import type { SelectOption } from '@shared/components/dropdown/dropdown.types';

export interface DialogCreerClientData {
  basesDeDonnees: string[];
  identifiantsExistants: string[];
}

@Component({
  selector: 'app-dialog-creer-client',
  imports: [
    ModalShellComponent,
    ModalFooterDirective,
    ButtonComponent,
    FormFieldComponent,
    FieldErrorDirective,
    FieldHintDirective,
    InputComponent,
    SelectComponent,
    ToggleComponent,
    ReactiveFormsModule,
  ],
  templateUrl: './dialog-creer-client.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './dialog-creer-client.component.scss',
})
export class DialogCreerClientComponent {
  // Bornes de saisie partagées avec le backend : une valeur trop longue serait refusée,
  // et surtout la modification ne pourrait pas être enregistrée dans le journal d'audit
  protected readonly longueurs = LONGUEURS_MAXIMALES;

  readonly dialogRef = inject<DialogRef<CreationClient, DialogCreerClientComponent>>(DialogRef);
  readonly data = inject<DialogCreerClientData>(DIALOG_DATA);

  // Un nouveau client n'a en général pas encore de base : bascule entre la sélection d'une
  // base existante du registre et la création d'une nouvelle (registre + secret Key Vault)
  creerNouvelleBase = signal<boolean>(false);

  clientForm = new FormGroup({
    identifiant: new FormControl<string>('', { nonNullable: true }),
    label: new FormControl<string>('', { nonNullable: true }),
    databaseName: new FormControl<string>('', { nonNullable: true }),
    managerIsEnabled: new FormControl<boolean>(false, { nonNullable: true }),
    meetchIsEnabled: new FormControl<boolean>(false, { nonNullable: true }),
    isOrange: new FormControl<boolean>(false, { nonNullable: true }),
    isSkidata: new FormControl<boolean>(false, { nonNullable: true }),
    isEventim: new FormControl<boolean>(false, { nonNullable: true }),
    meetchApiKey: new FormControl<string>('', { nonNullable: true }),
  });

  nouvelleBaseForm = new FormGroup({
    storeName: new FormControl<string>('', { nonNullable: true }),
    serveur: new FormControl<string>('', { nonNullable: true }),
    baseDeDonnees: new FormControl<string>('', { nonNullable: true }),
    utilisateur: new FormControl<string>('', { nonNullable: true }),
    motDePasse: new FormControl<string>('', { nonNullable: true }),
  });

  // `app-select` raisonne en options et en tableau de clés, et n'implémente pas
  // ControlValueAccessor : ces membres font la jonction avec le FormControl.
  // Déclarés après `clientForm`, dont ils dépendent à l'initialisation
  protected optionsBases = computed<SelectOption[]>(() =>
    this.data.basesDeDonnees.map(base => ({ value: base, label: base })),
  );

  // Miroir signal de `databaseName`, adossé à `valueChanges` : un FormControl
  // n'est pas réactif au sens des signaux
  private baseCourante = toSignal(this.clientForm.controls.databaseName.valueChanges, {
    initialValue: this.clientForm.controls.databaseName.value,
  });

  protected basesSelectionnees = computed<string[]>(() => {
    const base = this.baseCourante();
    return base ? [base] : [];
  });

  protected choisirBaseDeDonnees(valeurs: readonly string[]): void {
    this.clientForm.controls.databaseName.setValue(valeurs[0] ?? '');
  }

  annuler(): void {
    this.dialogRef.close();
  }

  // Miroirs signal des deux formulaires, lus par le gabarit et les prédicats ci-dessous
  protected readonly valeursClient = toSignal(
    this.clientForm.valueChanges.pipe(map(() => this.clientForm.getRawValue())),
    {
      initialValue: this.clientForm.getRawValue(),
    },
  );

  private readonly valeursNouvelleBase = toSignal(
    this.nouvelleBaseForm.valueChanges.pipe(map(() => this.nouvelleBaseForm.getRawValue())),
    { initialValue: this.nouvelleBaseForm.getRawValue() },
  );

  // L'identifiant doit être unique côté tenants (le doublon legacy est contrôlé au submit
  // par le backend, qui répond en conflit avec un message orientant vers l'import)
  readonly identifiantDejaUtilise = computed(() => {
    const identifiant = this.valeursClient().identifiant.trim().toLowerCase();
    return !!identifiant && this.data.identifiantsExistants.includes(identifiant);
  });

  // Le StoreName doit rester unique dans le registre Storage:Databases
  readonly storeNameDejaUtilise = computed(() => {
    const storeName = this.valeursNouvelleBase().storeName.trim().toLowerCase();
    return !!storeName && this.data.basesDeDonnees.some(base => base.toLowerCase() === storeName);
  });

  readonly peutValider = computed(() => {
    const client = this.valeursClient();
    const identifiantValide = !!client.identifiant.trim() && !this.identifiantDejaUtilise();

    if (!identifiantValide) {
      return false;
    }

    if (this.creerNouvelleBase()) {
      const base = this.valeursNouvelleBase();
      return !!base.storeName.trim() && !this.storeNameDejaUtilise() && !!base.serveur && !!base.baseDeDonnees;
    }

    return !!client.databaseName;
  });

  valider() {
    if (!this.peutValider()) {
      return;
    }

    const nouvelleBase = this.creerNouvelleBase();

    this.dialogRef.close({
      identifiant: this.clientForm.controls.identifiant.value.trim(),
      label: this.clientForm.controls.label.value || undefined,
      databaseName: nouvelleBase ? undefined : this.clientForm.controls.databaseName.value,
      baseACreer: nouvelleBase
        ? {
            storeName: this.nouvelleBaseForm.controls.storeName.value.trim(),
            serveur: this.nouvelleBaseForm.controls.serveur.value,
            baseDeDonnees: this.nouvelleBaseForm.controls.baseDeDonnees.value,
            utilisateur: this.nouvelleBaseForm.controls.utilisateur.value || undefined,
            motDePasse: this.nouvelleBaseForm.controls.motDePasse.value || undefined,
          }
        : undefined,
      managerIsEnabled: this.clientForm.controls.managerIsEnabled.value,
      meetchIsEnabled: this.clientForm.controls.meetchIsEnabled.value,
      isOrange: this.clientForm.controls.isOrange.value,
      isSkidata: this.clientForm.controls.isSkidata.value,
      isEventim: this.clientForm.controls.isEventim.value,
      meetchApiKey: this.clientForm.controls.meetchIsEnabled.value
        ? this.clientForm.controls.meetchApiKey.value || undefined
        : undefined,
    });
  }
}
